import { z } from 'zod';

export const WrappedResponseSchema = z.object({
  ai_tools: z.array(
    z.object({
      name: z.string(),
      frame_count: z.number(),
      percentage: z.number(),
      estimated_percentile: z.number().optional().default(0),
    })
  ),
  ai_hours: z.number().optional().default(0),
  daily_activity: z.array(
    z.object({ date: z.string(), frame_count: z.number() })
  ),
  days_active: z.number(),
  total_days: z.number(),
  longest_streak: z.number(),
  current_streak: z.number(),
  period_start: z.string(),
  period_end: z.string(),
  period_label: z.string(),
  peak_ai_date: z.string().nullable().optional(),
  peak_ai_hours: z.number().optional().default(0),
  estimated_top_percentile: z.number().nullable().optional(),
  estimated_hours_percentile: z.number().optional().default(0),
  estimated_streak_percentile: z.number().optional().default(0),
  estimated_activity_percentile: z.number().optional().default(0),
  persona: z.string(),
  headline: z.string(),

  top_tool: z
    .object({
      name: z.string(),
      capture_count: z.number(),
      share_of_ai: z.number(),
      first_seen: z.string().nullable(),
    })
    .nullable()
    .optional(),
  littlebird_usage: z
    .object({
      chat_count: z.number(),
      message_count: z.number(),
      longest_chat_messages: z.number(),
      meetings_recorded: z.number(),
      todos_created: z.number(),
      journals_created: z.number(),
      days_opened: z.number(),
    })
    .optional(),
  rhythm: z
    .object({
      peak_hour_local: z.number().nullable(),
      hour_persona: z.string(),
      weekend_share: z.number(),
      weekday_vs_weekend: z.string(),
    })
    .optional(),
  growth: z
    .object({
      ai_captures_growth: z.number(),
      chat_count_growth: z.number(),
      tools_new_this_month: z.array(z.string()),
      show_multiplier: z.boolean(),
    })
    .optional(),
  schema_version: z.number().optional(),
  owner_name: z.string().nullable().optional(),
});

export type WrappedResponse = z.infer<typeof WrappedResponseSchema>;

export type HeatmapCell = { date: string; level: 0 | 1 | 2 | 3 | 4 };

export type TopToolData = {
  name: string;
  captureCount: number;
  sharePct: number;
  firstSeen: string | null;
};

export type LittlebirdUsageData = {
  chatCount: number;
  messageCount: number;
  longestChatMessages: number;
  meetingsRecorded: number;
  todosCreated: number;
  journalsCreated: number;
  daysOpened: number;
};

export type RhythmData = {
  peakHourLocal: number | null;
  hourPersona: string;
  weekendShare: number;
  weekdayVsWeekend: string;
};

export type GrowthData = {
  aiCapturesGrowth: number;
  chatCountGrowth: number;
  toolsNewThisMonth: string[];
  showMultiplier: boolean;
};

export type WrappedReportData = {
  periodLabel: string;
  headline: string;
  persona: string;
  topPercentile: number | null;
  ownerName: string | null;

  topTool: TopToolData | null;
  tools: { name: string; percentage: number; captureCount: number }[];

  daysActive: number;
  totalDays: number;
  longestStreak: number;
  currentStreak: number;

  peakDayDate: string | null;
  heatmapWeeks: (HeatmapCell | null)[][];

  littlebirdUsage: LittlebirdUsageData;
  rhythm: RhythmData;
  growth: GrowthData;
};
